import { handleNavigationError } from './app/routing/navigation-error-handler';
import { provideRouteScrollReset } from './app/routing/route-scroll-reset';
import { provideLaunchSplashDismissal } from './app/routing/launch-splash';
import { enableProdMode, provideZonelessChangeDetection } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, withInMemoryScrolling, withRouterConfig, withNavigationErrorHandler } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideTanStackQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { persistQueryClientRestore, persistQueryClientSubscribe } from '@tanstack/query-persist-client-core';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { provideIcons } from '@ng-icons/core';
import {
  lucideChevronDown,
  lucideCircleCheck,
  lucideClock,
  lucideEllipsisVertical,
  lucideListChecks,
  lucideListFilter,
  lucideLock,
  lucidePencil,
  lucideSearch,
  lucideSparkles,
  lucideX,
} from '@ng-icons/lucide';

import { environment } from './environments/environment';
import { MobileService } from './app/services/mobile.service';
import { backendErrorInterceptor } from './app/services/backend-error.interceptor';
import { authInterceptor } from './app/services/auth.interceptor';
import { AppComponent } from './app/app.component';
import { routes } from './app/routing/app.routes';
import { NativeLinkService } from './app/services/native-link.service';
import { consumePromoCodeFromLocation } from './app/services/pending-promo-code.service';

// Optimized builds are already in production mode; this keeps unoptimized
// production-environment runs (the local-prod configuration) out of dev mode too.
if (environment.production) enableProdMode();

const queryCacheMaxAge = 1000 * 60 * 60 * 24;
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: queryCacheMaxAge,
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000 * 60,
    },
  },
});
const getLocalStorage = (): Storage | undefined => {
  if (!environment.localStorage) return undefined;

  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
};
const queryPersister = createAsyncStoragePersister({
  key: 'BUDGETS_QUERY_CACHE',
  storage: getLocalStorage(),
});
const queryPersistenceOptions = {
  // Cached bucket/onboarding responses from the category API have incompatible field names.
  buster: 'budgets-query-v2-groups',
  maxAge: queryCacheMaxAge,
  persister: queryPersister,
  queryClient,
};

// Before routing can rewrite the URL: keep a `?promo=` code from an ad link.
consumePromoCodeFromLocation();

async function startApp(): Promise<void> {
  await persistQueryClientRestore(queryPersistenceOptions).catch(() => undefined);
  persistQueryClientSubscribe(queryPersistenceOptions);

  const appRef = await bootstrapApplication(AppComponent, {
    providers: [
      provideZonelessChangeDetection(),
      provideHttpClient(withInterceptors([backendErrorInterceptor, authInterceptor])),
      provideTanStackQuery(queryClient),
      provideRouter(
        routes,
        withRouterConfig({ paramsInheritanceStrategy: 'always' }),
        withNavigationErrorHandler(handleNavigationError),
        withInMemoryScrolling({ scrollPositionRestoration: 'disabled' }),
      ),
      provideRouteScrollReset(),
      provideLaunchSplashDismissal(),
      provideServiceWorker('ngsw-worker.js', { enabled: environment.serviceWorker }),
      provideIcons({
        lucideChevronDown,
        lucideCircleCheck,
        lucideClock,
        lucideEllipsisVertical,
        lucideListChecks,
        lucideListFilter,
        lucideLock,
        lucidePencil,
        lucideSearch,
        lucideSparkles,
        lucideX,
      }),
    ],
  });

  const mobileService = appRef.injector.get(MobileService);

  (window as typeof window & { BudgetsMobileAppService?: MobileService }).BudgetsMobileAppService = mobileService;
  mobileService.initializeNativeBridge();
  appRef.injector.get(NativeLinkService).initializeNativeBridge();
}

startApp().catch(err => console.log(err));
